import {
  type CheckboxOptionType,
  Radio, type RadioChangeEvent, type RadioGroupProps, Select, type SelectProps,
} from 'antd';
import { useCallback, useEffect } from 'react';

type OnChangeType<T> = T extends { mode: 'multiple' | 'tags' }
  ? (value: string[]) => void
  : (value: string) => void;

export interface RemoteSearchableDropdownInputProps extends Omit<SelectProps, 'onChange'> {
  onChange?: OnChangeType<SelectProps>;
  optionsLoading?: boolean;
  prefetchOptions?: boolean;
  handleFetchOptions?: Function;
  handleFocus?: Function;
  searchString?: string;
  setSearchString?: (value: string) => void;
  showRadioGroup?: boolean;
  radioGroupProps?: RadioGroupProps;
}

export default function RemoteSearchableDropdownInput({
  onChange,
  value,
  options,
  optionsLoading,
  prefetchOptions,
  handleFetchOptions,
  handleFocus,
  searchString,
  setSearchString,
  mode,
  disabled,
  showRadioGroup,
  radioGroupProps,
  ...props
}: RemoteSearchableDropdownInputProps) {
  const handleClick = useCallback((newValue: string | string[]) => {
    if (onChange) {
      if (mode === 'multiple' || mode === 'tags') {
        (onChange as unknown as (value: string[]) => void)(newValue as string[]);
      } else {
        onChange(newValue as string);
      }
    }
  }, [onChange, mode]);

  const handleRadioChange = useCallback(
    (e: RadioChangeEvent) => {
      if (onChange) onChange(e.target.value);
    },
    [onChange],
  );

  const handleSearch = useCallback((searchValue: string) => {
    if (setSearchString) {
      setSearchString(searchValue);
    }
  }, [setSearchString]);

  const fetchOptions = useCallback(() => {
    if (!options?.length && handleFetchOptions) {
      handleFetchOptions();
    }
    if (handleFocus) handleFocus();
  }, [handleFetchOptions, handleFocus, options?.length]);

  useEffect(() => {
    // If prefetchOptions is true, fetch options on mount
    if (prefetchOptions) fetchOptions();
  }, [fetchOptions, prefetchOptions]);

  if (showRadioGroup && prefetchOptions) {
    return (
      <Radio.Group
        value={value}
        onChange={handleRadioChange}
        disabled={disabled}
        options={options?.length ? options as CheckboxOptionType[] : []}
        {...radioGroupProps}
      />
    );
  }
  return (
    <Select
      value={value}
      mode={mode}
      placeholder="Select a Value, Type to Search"
      optionFilterProp="label"
      options={options?.length ? options : []}
      loading={optionsLoading}
      onChange={handleClick}
      onFocus={fetchOptions}
      showSearch
      searchValue={searchString}
      onSearch={handleSearch}
      disabled={disabled}
      {...props}
    />
  );
}
